<template>
  <div class="text-center mt-6" data-testid="exercise_v2_mount">
    <MicCheck
      v-if="needs_mic_check && instance"
      ref="mic_check"
      :instance="instance"
      @granted="mic_granted = true"
      @denied="mic_granted = false"
    />
    <v-progress-circular
      v-if="!exercise_loaded"
      :size="70"
      color="primary"
      indeterminate
      data-testid="exercise_v2_mount_loading"
    />
    <v-btn v-if="can_show_start" x-large dark color="green" @click="start_exercise" style="outline: 1px solid black"
      data-testid="exercise_v2_mount_start">
      <v-icon>mdi-play</v-icon>
      {{ $t("common.start") }}
    </v-btn>
  </div>
</template>

<script>
import Vue from "vue";
import { getExerciseDefaultConfig } from "../classes/exercise";
import { is_tauri, tauri_start_fullscreen, tauri_exit_fullscreen } from "../lib/tauri";
import { defaultAnaglyphOrientation } from "../lib/anaglyph_orientation";
import { mapState } from "vuex";
import MicCheck from "./MicCheck.vue";

export default {
  components: { MicCheck },
  props: {
    fullscreen_enabled: {
      type: Boolean,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    exercise: {
      type: Object,
      required: true,
    },
    compat: {
      type: Boolean,
      default: () => false,
    },
    unique_id: {
      type: String,
      required: true,
    },
    user_id: {
      type: String,
      default() {
        return "";
      },
    },
    instance_id: {
      type: String,
      default() {
        return "";
      },
    },
  },
  data() {
    return {
      exercise_loaded: false,
      mic_granted: false,
      instance: null,
      container: null,
    };
  },
  computed: {
    ...mapState(["app_config", "current_clinic_prefs"]),
    can_show_start() {
      if (!this.exercise_loaded) {
        return false;
      }
      if (!this.needs_mic_check) {
        return true;
      }
      return this.mic_granted;
    },
    needs_mic_check() {
      if (!this.exercise.speech_to_text) {
        return false;
      }
      const variants = this.exercise.speech_to_text_variants;
      if (variants == null) {
        return true;
      }
      const variant = this.config && this.config.variant;
      return Array.isArray(variants) && variants.includes(variant);
    },
    sanitized_config() {
      const config = this.config || {};
      const template = (this.exercise && getExerciseDefaultConfig(this.exercise, window.OpticalGymApp)) || {};
      const sanitized = {};

      for (const key of Object.keys(config)) {
        sanitized[key] = this.coerce_value(config[key], template[key]);
      }

      return sanitized;
    },
  },
  mounted() {
    this.create_exercise_container();
    this.load_exercise();
  },
  methods: {
    reset: function () {
      this.exercise_loaded = false;
      this.mic_granted = false;
      this.remove_exercise_parent();
    },
    create_exercise_container() {
      this.remove_exercise_parent();
      this.container = document.createElement("div");
      this.container.id = "opticalgym-exercise-container";
      this.container.style.display = "none";
      document.body.appendChild(this.container);
    },
    async load_exercise() {
      try {
        // Use new Function to dynamically create the import statement
        const importPath = `/api/exercise/${this.exercise.id}/${this.exercise.id}.js`;
        const module = await new Function(`return import('${importPath}')`)();
        const ExerciseClass = module.default;
        this.initialize_exercise(ExerciseClass);
      } catch (error) {
        console.error("Failed to load exercise module:", error);
      }
    },
    initialize_exercise(ExerciseClass) {
      let exercise_instance = new ExerciseClass({
        params: this.sanitized_config,
        lang: this.$i18n.lang(),
        parent: "opticalgym-exercise-container",
        asset_path: `/api/exercise/${this.exercise.id}/assets/`,
        stereo_mode: (this.app_config && this.app_config.stereo_mode) || "anaglyph",
        anaglyph_preferences: {
          default_orientation: defaultAnaglyphOrientation(
            this.current_clinic_prefs,
            this.app_config,
          ),
          disallow_different_glasses: Boolean(
            this.current_clinic_prefs?.disallow_different_glasses,
          ),
        },
        user_id: this.user_id || undefined,
        instance_id: this.instance_id || undefined,
        ready: () => {
          this.exercise_loaded = true;
        },
        finished: (report, reason) => {
          if (reason === "complete") {
            this.$emit("exercise-finished", {
              report: report,
              score: report.score,
              level_completed: report.level_completed || false,
              type: "interactive",
            });
          } else {
            // Exited early: the partial score is shown but never saved or counted as a completion.
            this.$emit("exercise-incomplete", {
              report: report,
              score: report.score,
              level_completed: false,
              type: "interactive",
            });
          }
          this.clear_exercise();
        },
      });
      this.instance = exercise_instance;
      window.OpticalGymExerciseInstance = exercise_instance;

      // If we're in debug, create a finalization registry
      if (this.$store.state.debug) {
        window.exerciseFinalizationRegistry = new FinalizationRegistry((heldValue) => {
          console.log("Confirmed deletion and GC:", heldValue);
        });
        window.exerciseFinalizationRegistry.register(exercise_instance, "OpticalGymExerciseInstance");
      }
    },
    start_exercise() {
      if (this.instance) {
        const mic_check = this.$refs.mic_check;
        if (mic_check) {
          if (typeof this.instance.setSpeechToTextDeviceId === "function") {
            this.instance.setSpeechToTextDeviceId(mic_check.get_device_id());
          }
          mic_check.stop();
        }
        if (is_tauri()) {
          tauri_start_fullscreen();
        }
        this.instance.start();
      }
    },
    clear_exercise: function () {
      this.exercise_loaded = false;
      this.mic_granted = false;
      const mic_check = this.$refs.mic_check;
      if (mic_check && typeof mic_check.stop === "function") {
        mic_check.stop();
      }
      this.instance = null;
      this.remove_exercise_parent();
      window.OpticalGymExerciseInstance = null;
      if (is_tauri()) {
        tauri_exit_fullscreen();
      }
    },
    remove_exercise_parent() {
      if (this.container) {
        document.body.removeChild(this.container);
        this.container = null;
      }

      // Check if there's a leftover instance of a previous exercise
      const container = document.getElementById("opticalgym-exercise-container");
      if (container) {
        container.parentNode.removeChild(container);
      }
    },
    coerce_value(value, template_value) {
      if (template_value === undefined || template_value === null) {
        return value;
      }

      if (Array.isArray(template_value)) {
        if (Array.isArray(value)) {
          return value;
        }
        if (value === undefined || value === null) {
          return [];
        }
        return [value];
      }

      switch (typeof template_value) {
        case "number": {
          if (typeof value === "number") {
            return value;
          }
          const coerced = Number(value);
          return Number.isNaN(coerced) ? value : coerced;
        }
        case "boolean": {
          if (typeof value === "boolean") {
            return value;
          }
          if (typeof value === "string") {
            const normalized = value.trim().toLowerCase();
            if (["true", "1", "yes", "on"].includes(normalized)) {
              return true;
            }
            if (["false", "0", "no", "off"].includes(normalized)) {
              return false;
            }
          }
          if (typeof value === "number") {
            return value !== 0;
          }
          return Boolean(value);
        }
        case "string": {
          if (value === null || value === undefined) {
            return value;
          }
          return String(value);
        }
        case "object": {
          if (value === null || typeof value !== "object") {
            return value;
          }
          const sanitized_object = {};
          for (const key of Object.keys(value)) {
            sanitized_object[key] = this.coerce_value(
              value[key],
              template_value[key]
            );
          }
          return sanitized_object;
        }
        default:
          return value;
      }
    },
  },
};
</script>
